import { motion } from "framer-motion";
import { ArrowRight, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { UnlockStatus } from "@/components/UnlockStatus";
import { ArtworkImage } from "@/components/ArtworkImage";
import { ARTWORK_SIZES } from "@/config/artwork";
import { lockedMessage, useUnlock } from "@/context/TokenDataContext";

export const EcosystemCard = ({ product, index }) => {
  const { isUnlocked, unlockLabel } = useUnlock();

  const handleClick = () => {
    if (!isUnlocked) {
      toast(lockedMessage("product"), { icon: <Lock className="h-4 w-4 text-gold" /> });
      return;
    }
    toast.success(`${product.name} is unlocked and available.`);
  };

  return (
    <motion.article
      className={`eco-card ${isUnlocked ? "eco-card--unlocked" : "eco-card--locked"}`}
      style={{ "--card-accent": product.color }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      data-testid={`ecosystem-card-${product.id}`}
      data-state={isUnlocked ? "unlocked" : "locked"}
    >
      <div className="eco-card__accent" />
      <div
        className={`eco-card__media${product.image.unobscured ? " eco-card__media--unobscured" : ""}`}
        style={product.image.useNativeRatio ? { aspectRatio: `${product.image.width} / ${product.image.height}`, flexShrink: 0 } : undefined}
        data-testid={`ecosystem-card-${product.id}-media`}
      >
        <ArtworkImage
          artwork={product.image}
          sizes={ARTWORK_SIZES.ecosystem}
          testId={`ecosystem-card-${product.id}-image`}
          descriptionId={product.image.caption ? `ecosystem-card-${product.id}-image-caption` : undefined}
        />
        <div className="eco-card__lockbadge" aria-hidden="true">
          {isUnlocked ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 px-5 pb-5 pt-1">
        <div className="flex-1 text-center">
          <h3 className="font-display text-[1rem] font-extrabold tracking-wide sm:text-[1.05rem]" style={{ color: product.color, textShadow: `0 0 16px ${product.color}66` }} data-testid={`ecosystem-card-${product.id}-title`}>
            {product.name}
          </h3>
          <p className="mt-1 font-body text-[0.72rem] font-semibold tracking-[0.16em] text-white/75">{product.subtitle}</p>
          {product.image.caption && (
            <p
              id={`ecosystem-card-${product.id}-image-caption`}
              data-testid={`ecosystem-card-${product.id}-image-caption`}
              className="mt-2 font-body text-xs font-semibold leading-relaxed text-gold"
            >
              {product.image.caption}
            </p>
          )}
        </div>
        <UnlockStatus variant="full" testId={`ecosystem-card-${product.id}-unlock-status`} />
        <button
          type="button"
          onClick={handleClick}
          aria-disabled={!isUnlocked}
          className={isUnlocked ? "btn-neon w-full !text-[0.72rem]" : "btn-locked"}
          data-testid={`ecosystem-card-${product.id}-button`}
        >
          {isUnlocked ? (
            <>EXPLORE <ArrowRight className="h-4 w-4" /></>
          ) : (
            <><Lock className="h-3.5 w-3.5" /> LOCKED · UNLOCKS AT {unlockLabel}</>
          )}
        </button>
        <p className="text-center font-body text-sm font-medium text-white/60">{product.tagline}</p>
      </div>
    </motion.article>
  );
};
